import React, { useState } from "react";

import { useMutation } from "@apollo/client";
import CheckIcon from "@mui/icons-material/Check";
import { Box, Button, Modal, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";

import FormBuilder from "../components/FormBuilder";
import Form from "../components/FormBuilder/store/Form";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    margin: 14,
    height: "100%",
  },
  navBar: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
  },
  subNav: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
  },
  componentsContainer: {
    backgroundColor: colors.white,
    height: "100%",
    width: "30%",
    marginRight: 14,
  },
  formContainer: {
    backgroundColor: colors.white,
    width: "70%",
    height: "100%",
  },
  grid: {
    flexGrow: 1,
    margin: theme.spacing(4, 2, 2, 2),
    backgroundColor: "#333",
  },
  formRoot: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    height: "100%",
    marginTop: 8,
  },
  button: {
    marginLeft: 10,
    width: theme.typography.pxToRem(58),
    alignSelf: "center",
  },
  saveModal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingBottom: 12,
  },
  icon: {
    color: colors.white,
    fontSize: "30px",
  },
  iconContainer: {
    height: "46px",
    width: "46px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    marginBottom: "20px",
    backgroundColor: "#26C9A7",
    marginTop: 12,
  },
}));

const createFormMutation = graphql(`
  mutation CreateForm($input: CreateFormInput!) {
    createForm(input: $input) {
      form {
        id
      }
    }
  }
`);

const CreateFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { classes } = useStyles();
  const [createForm] = useMutation(createFormMutation);
  const [isConfirmationModalOpened, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [createdFormId, setCreatedFormId] =
    useState<string | undefined>(undefined);

  function handleConfirmationModalOpen() {
    setIsConfirmationModalOpen(true);
  }

  function handleConfirmationModalClose() {
    if (createdFormId) {
      navigate(`/forms/${createdFormId}/edit`);
    }
    setIsConfirmationModalOpen(false);
  }

  const snackBar = useGlobalSnackbar();
  function onGoback() {
    navigate("/forms");
  }

  const [form] = useState(new Form(1));

  const onSubmit = async () => {
    const input = form.toCreateFormInput();
    try {
      const result = await createForm({ variables: { input: input } });
      if (result.data) {
        setCreatedFormId(result.data.createForm?.form?.id);
        handleConfirmationModalOpen();
      }
    } catch (error) {
      snackBar.open(`There was an error in creating your form ${error}`);
    }
  };

  return (
    <div className={classes.root}>
      <FormBuilder form={form} onSave={onSubmit} onCancel={onGoback} />

      <Modal
        className={classes.saveModal}
        open={isConfirmationModalOpened}
        onClose={handleConfirmationModalClose}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          style={{
            backgroundColor: colors.white,
            height: "auto",
            width: "auto",
            paddingTop: 8,
            paddingRight: 18,
            paddingBottom: 18,
            paddingLeft: 18,
          }}
        >
          <Box className={classes.iconContainer}>
            <CheckIcon className={classes.icon} />
          </Box>
          <Typography variant="h6" color="textPrimary">
            Successful!
          </Typography>
          <Typography
            variant="body1"
            color="textPrimary"
            style={{ marginTop: 8, marginBottom: 8 }}
          >
            Your form has been successfully created!
          </Typography>

          <Button onClick={handleConfirmationModalClose}>Ok</Button>
        </Box>
      </Modal>
    </div>
  );
};

export default CreateFormPage;
